import type { Messages } from "../catalogue.en";

/**
 * Clinical Summary: the patient card's AI summary — its build act, version
 * strip, Word download and the clinic letterhead it is laid out on.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const clinicalSummary: Partial<Messages> = {
  "patient.summary.title": "Клиническое саммари",
  "patient.summary.description": "Сформировано ИИ по клиническим данным карты и документам пациента.",
  "patient.summary.build": "Построить клиническое саммари",
  "patient.summary.update": "Обновить саммари",
  "patient.summary.full": "Построить заново",
  "patient.summary.history": "История",
  "patient.summary.language": "Язык саммари",
  "patient.summary.cancel": "Отменить формирование",
  "patient.summary.empty": "Клиническое саммари ещё не сформировано.",
  "patient.summary.busy": "Обработано фрагментов: {done} из {total}",
  "patient.summary.failed": "Формирование завершилось ошибкой. Предыдущее саммари сохранено.",
  "patient.summary.cancelled": "Формирование отменено. Предыдущее саммари сохранено.",
  "patient.summary.showEmpty": "Показать пустые разделы ({count})",
  "patient.summary.sources": "Источники",
  "patient.summary.modeFull": "Построено с нуля",
  "patient.summary.modeUpdate": "Дополнено новыми данными",
  "patient.summary.modeCoverage": "Обновлён список источников",
  "patient.summary.modeUnchanged": "Без изменений",
  "patient.summary.more": "Загрузить более ранние редакции",
  "patient.summary.readDenied": "Нет доступа ко всему составу источников клинического саммари.",
  "patient.summary.requestFailed": "Не удалось выполнить действие. Попробуйте ещё раз.",
  "patient.summary.warnings": "Комментарии ИИ к подготовке саммари",
  "patient.summary.menu": "Действия с клиническим саммари",
  "patient.summary.source": "Открыть",
  "patient.summary.noSources": "Нет пригодных клинических данных или текста документов.",
  "patient.summary.unknownModel": "У выбранной модели нет проверенного профиля возможностей. Выберите поддержанную модель в настройках ИИ.",
  "patient.summary.limit": "Достигнут предел обработки. Предыдущее саммари сохранено.",
  "patient.summary.latestLanguages": "Последние редакции по языкам",
  "patient.summary.languageResourcesInvalid": "Языковые материалы саммари отсутствуют или повреждены. Попросите администратора проверить вспомогательные файлы ИИ.",
  "patient.summary.busyTitle": "Формируется клиническое саммари…",
  "patient.summary.emptyDetail": "Сверьте источники и запустите формирование: ИИ соберёт саммари из выбранных данных карты и документов; источники каждой редакции сохраняются.",
  "patient.summary.stateLatest": "Актуальная редакция",
  "patient.summary.stateHistorical": "Историческая редакция",
  "patient.summary.backToLatest": "К актуальной редакции",
  "patient.summary.changedRetry": "Источники или выбор изменились во время запуска. Загрузите текущий состав, сверьте его и попробуйте ещё раз.",
  "patient.summary.aiProjectUnavailable": "Для организации ещё не настроен проект ИИ. Попросите администратора завершить настройку ИИ организации.",
  "patient.summary.keyMissing": "Добавьте свой ключ OpenAI в настройках учётной записи, прежде чем формировать саммари.",
  "patient.summary.functionDisabled": "Формирование клинического саммари отключено для этой клиники в Центре управления.",
  "patient.summary.serviceUnavailable": "Сервис ИИ сейчас недоступен. Попробуйте позже.",
  "patient.summary.generateDenied": "У вас нет права запускать формирование саммари.",
  "patient.summary.download": "Скачать в Word",
  "patient.summary.downloadFailed": "Не удалось подготовить документ Word. Попробуйте ещё раз.",
  "patient.summary.downloadLetterhead": "Не удаётся прочитать бланк организации. Замените или удалите его в меню саммари.",
  "patient.summary.downloadUnavailable": "Сервис документов сейчас недоступен. Попробуйте позже.",
  "patient.summary.letterhead": "Бланк организации",
  "patient.summary.letterheadLoaded": "Загружен: {name}. Применяется к документам Word всех пациентов.",
  "patient.summary.letterheadNone": "Не загружен. В документах Word — стандартная шапка с реквизитами организации.",
  "patient.summary.letterheadUpload": "Загрузить бланк (DOCX)",
  "patient.summary.letterheadReplace": "Заменить бланк",
  "patient.summary.letterheadRemove": "Удалить бланк",
  "patient.summary.letterheadPicker": "Файл бланка",
  "patient.summary.letterheadRemoveTitle": "Удалить бланк организации?",
  "patient.summary.letterheadRemoveDescription": "Документы Word всех пациентов будут выходить со стандартной шапкой с реквизитами организации.",
  "patient.summary.letterheadRemoveConfirm": "Удалить",
  "patient.summary.letterheadKeep": "Оставить",
  "patient.summary.letterheadFailed": "Не удалось сохранить бланк. Попробуйте ещё раз.",
  "patient.summary.letterheadInvalid": "Файл не является документом Word (DOCX).",
  "patient.summary.letterheadActive": "Бланк содержит макросы, встроенные объекты или внешнее содержимое. Сохраните его как обычный DOCX и загрузите снова.",
  "patient.summary.letterheadTooLarge": "Файл бланка должен быть не больше 4 МБ.",
  "patient.summary.letterheadChanged": "Бланк только что изменил другой пользователь. Показано текущее состояние, повторите действие.",
  "patient.summary.letterheadQuota": "Хранилище организации заполнено, бланк не сохранён.",
  "patient.summary.letterheadDenied": "У вас нет права менять бланк организации.",
  "patient.summary.compose": "Выбрать источники",
  "patient.summary.composeShared": "Выбор общий для всех врачей и всех языков саммари этого пациента. Снятые источники не передаются ИИ.",
  "patient.summary.saveChoices": "Сохранить выбор",
  "patient.summary.saveAndBuild": "Сохранить и построить",
  "patient.summary.saveAndUpdate": "Сохранить и обновить",
  "patient.summary.starting": "Запуск…",
  "patient.summary.choicesSaved": "Выбор сохранён. Формирование не запускалось.",
  "patient.summary.notStarted": "Формирование не запущено; сохранённый выбор остаётся в силе.",
  "patient.summary.composeWhileRunning": "Идёт формирование. Выбор можно сохранить сейчас — он применится при следующем построении.",
  "patient.summary.excludedChanged": "Изменился после исключения",
  "patient.summary.reviewNeeded": "Исключённые источники изменились",
  "patient.summary.reviewNeededDetail": "Сверьте выбор источников. Сохранение подтверждает исключения в показанном виде и не запускает формирование.",
  "patient.summary.reviewSources": "Сверить источники",
  "patient.summary.choicesChanged": "Выбор или источники изменились с момента показа этого списка. Загрузите текущий состав и сверьте его; несохранённые изменения остаются до загрузки.",
  "patient.summary.loadCurrent": "Загрузить текущий состав",
  "patient.summary.reloaded": "Показан текущий состав; сверьте его ещё раз.",
  "patient.summary.saveChoicesDenied": "У вас нет права менять выбор источников.",
  "patient.summary.unsavedChoices": "Несохранённый выбор источников клинического саммари",
  "patient.summary.sourcesCount": "Источники · {count}",
  "patient.summary.untitled": "Без названия",
  "patient.summary.kindChart": "Титульный лист и анамнез",
  "patient.summary.kindRecords": "Записи ЭМК",
  "patient.summary.kindDocuments": "Документы",
  "patient.summary.kindLaboratory": "Результаты анализов",
  "patient.summary.kindVitals": "Витальные показатели",
  "patient.summary.kindReferrals": "Направления",
  "patient.summary.kindStays": "Госпитализации",
  "patient.summary.kindOther": "Другое",
  "patient.summary.kindCount": "{kind}: {count}",
  "patient.summary.measurement": "Измерение",
  "patient.summary.measurementValues": "показателей: {count}",
  "patient.summary.reasonGone": "Больше не доступен",
  "patient.summary.composeTotal": "Будет учтено: {included} из {total}",
  "patient.summary.groupCount": "{included} из {total}",
  "patient.summary.groupTick": "Учитывать: {group}",
  "patient.summary.versionUsed": "Учтено источников: {count}",
  "patient.summary.versionContents": "Состав",
  "patient.summary.period": "Данные с {from} по {to}",
  "patient.summary.periodDay": "Данные за {date}",
  "patient.summary.changedTitle": "После построения этой редакции карта изменилась",
  "patient.summary.changedAdded": "новых источников: {count}",
  "patient.summary.changedEdited": "изменилось: {count}",
  "patient.summary.changedRemoved": "больше не учитывается: {count}",
  "patient.summary.reviewNeededInline": "Исключённые источники изменились — сверьте выбор при обновлении.",
};
